import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Tabs,
  Tab,
  Box,
  Switch,
  FormControlLabel,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Language as LanguageIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import { useSelectedOrganization } from '../../hooks/useSelectedOrganization';
import { useOrganizationLocations } from '../../hooks/useOrganizationData';
import { useStoreWebsite } from './hooks/useStoreWebsite';
import { storeWebsiteApi } from './services/storeWebsiteApi';

import DomainConfiguration from './components/DomainConfiguration';
import ThemeEditor from './components/ThemeEditor';
import SEOSettings from './components/SEOSettings';
import SocialLinks from './components/SocialLinks';
import CMSPageList from './pages/CMSPageList';

import FormButton from '../../components/common/stories/FormButton';
import '../settings/Settings.css';
import './StoreWebsiteSettings.css';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`website-tabpanel-${index}`}
      aria-labelledby={`website-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const StoreWebsiteSettings = ({ userOrganizationId }) => {
  const { isAdmin, isLoading: isUserLoading } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeOrganizationId = useSelectedOrganization(userOrganizationId);
  const notification = useNotification();

  const { data: locations = [], isLoading: isLoadingLocations } =
    useOrganizationLocations(activeOrganizationId);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const {
    website,
    pages,
    isLoading: isLoadingWebsite,
    isSaving,
    createWebsite,
    updateWebsite,
    togglePublish,
    getPublicUrl,
    platformDomain,
  } = useStoreWebsite(selectedLocationId);

  // Redirect non-admins
  useEffect(() => {
    if (!isUserLoading && !isAdmin()) {
      notification.error('You do not have permission to access this page');
      navigate('/dashboard');
    }
  }, [isAdmin, isUserLoading, navigate, notification]);

  // Pick a default location once they load (respecting the ?location= param)
  useEffect(() => {
    if (!locations.length) return;
    const locationParam = searchParams.get('location');
    if (locationParam && locations.find((l) => l._id === locationParam)) {
      setSelectedLocationId(locationParam);
    } else {
      setSelectedLocationId(locations[0]._id);
    }
  }, [locations, searchParams]);

  const selectedLocation = locations.find((l) => l._id === selectedLocationId);

  const handleCreateWebsite = async () => {
    if (!selectedLocation) return;

    const orgSlug = storeWebsiteApi.generateSlug(selectedLocation.store_name || 'store');
    const subdomainSlug = selectedLocation.city
      ? storeWebsiteApi.generateSlug(selectedLocation.city)
      : 'main';

    await createWebsite({
      organization_id: activeOrganizationId,
      subdomain_slug: subdomainSlug,
      org_slug: orgSlug,
      is_published: false,
      theme: {
        primary_color: '#ff8264',
        logo_url: '',
        banner_image_url: '',
      },
      seo: {
        title: selectedLocation.store_name || 'Store',
        description: `Weekly flyers and deals at ${selectedLocation.store_name}`,
        og_image_url: '',
      },
      social: {
        facebook: '',
        instagram: '',
        twitter: '',
      },
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleLocationChange = (e) => {
    setSelectedLocationId(e.target.value);
    setActiveTab(0);
  };

  const publicUrl = getPublicUrl();

  if (isUserLoading || isLoadingLocations) {
    return (
      <div className="dashboard-body-wrapper">
        <div className="main-column">
          <div className="div-block">
            <span className="div-block-title">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="dashboard-body-wrapper">
        <div className="main-column">
          <div className="div-block">
            <span className="div-block-title">Website Management</span>
          </div>
          <div className="div-block">
            <div className="info-text">
              <p>You need to create at least one store location before you can set up a website.</p>
              <FormButton onClick={() => navigate('/settings/locations')}>
                Manage Locations
              </FormButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-body-wrapper">
      <div className="main-column">
        <div className="div-block">
          <div className="website-header">
            <span className="div-block-title">
              <LanguageIcon style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Website Management
            </span>
            {website && (
              <div className="website-status">
                <FormControlLabel
                  control={
                    <Switch
                      checked={website.is_published}
                      onChange={togglePublish}
                      disabled={isSaving}
                      color="primary"
                    />
                  }
                  label={website.is_published ? 'Published' : 'Unpublished'}
                />
                {publicUrl && (
                  <Tooltip title="View Website">
                    <IconButton
                      onClick={() => window.open(publicUrl, '_blank')}
                      size="small"
                    >
                      <OpenInNewIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Location Selector */}
        <div className="div-block">
          <div className="location-selector">
            <label htmlFor="location-select">Select Location:</label>
            <select
              id="location-select"
              value={selectedLocationId || ''}
              onChange={handleLocationChange}
              className="location-dropdown"
            >
              {locations.map((location) => (
                <option key={location._id} value={location._id}>
                  {location.store_name}
                  {location.city && ` - ${location.city}`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoadingWebsite ? (
          <div className="div-block">
            <div style={{ textAlign: 'center', padding: 40 }}>
              <CircularProgress />
            </div>
          </div>
        ) : !website ? (
          // No website exists - show create option
          <div className="div-block">
            <div className="create-website-prompt">
              <h3>Create a Website for {selectedLocation?.store_name}</h3>
              <p>
                Set up a public website to showcase your weekly flyers and store information.
                Customers will be able to view your current flyer, store hours, and contact details.
              </p>
              <FormButton onClick={handleCreateWebsite} disabled={isSaving}>
                {isSaving ? 'Creating...' : 'Create Website'}
              </FormButton>
            </div>
          </div>
        ) : (
          // Website exists - show management tabs
          <>
            {publicUrl && (
              <div className="div-block">
                <div className="website-url-display">
                  <span className="url-label">Your website URL:</span>
                  <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="url-link">
                    {publicUrl}
                    <OpenInNewIcon style={{ fontSize: 14, marginLeft: 4 }} />
                  </a>
                </div>
              </div>
            )}

            <div className="div-block">
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="Website settings tabs"
              >
                <Tab label="Domain" />
                <Tab label="Theme & Branding" />
                <Tab label="SEO" />
                <Tab label="Social Links" />
                <Tab label="Pages" />
              </Tabs>

              <TabPanel value={activeTab} index={0}>
                <DomainConfiguration
                  website={website}
                  onUpdate={updateWebsite}
                  isSaving={isSaving}
                  platformDomain={platformDomain}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <ThemeEditor
                  website={website}
                  onUpdate={updateWebsite}
                  isSaving={isSaving}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                <SEOSettings
                  website={website}
                  onUpdate={updateWebsite}
                  isSaving={isSaving}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={3}>
                <SocialLinks
                  website={website}
                  onUpdate={updateWebsite}
                  isSaving={isSaving}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={4}>
                <CMSPageList
                  websiteId={website._id}
                  pages={pages}
                  locationId={selectedLocationId}
                />
              </TabPanel>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StoreWebsiteSettings;
